import { ITool } from '@atredux/slices/toolSlice'
import { EnvironmentDetail } from '@amzn/ihm-associate-tools-js-lib'
import { generateDeferredPromise } from '@utils/DeferredPomiseUtil'
import { getDomain, getStoreId, _getParameterByName, getRegion, getRegionForStore, getDomainFromHostname } from '@utils/EnvironmentUtil'
import { getLocalDevConfig as _getLocalDevConfig, devLog } from '@src/config/localDevConfig'
import { getToolById } from '@crossframe/apis/ToolRegistration'
import { store } from '@atredux/store'
import { getStoreInfo, getStoreId as getStoreIdFromRedux, getSelectedStoreId, getStoreScanStatus } from '@atredux/selectors/storeSelector'
import { ISubzone, IZone } from '@atredux/slices/zoneSlice'
import { getSelectedTool } from '@atredux/selectors/toolSelector'
import { getSelectedLocale } from '@atredux/selectors/localeSelector'
import { NO_CURRENT_STORE } from '@src/constants'
import { RegisteredAssetsProvider, AssetRegistration, getAssetDomain, PlatformDomain, Domain, resolveSupportedPlatforms } from '@amzn/jwo-store-management-platform-registration'
import { getAssetBaseUrl, getAssetIconUrl } from '@utils/AssetUrlUtil'
import { getLaunchStoreId } from '@utils/LaunchStore'
import { isReactNativeApp } from '@amzn/jwo-store-management-platform-components'
import { featureFlagService } from '@services/featureFlags/featureFlagService'
import _ from 'lodash'

/** AppConfig flag gating the whole view-only tier (on in alpha/beta/gamma, off in prod). */
export const VIEW_ONLY_TOOLS_FLAG = 'view_only_tools_v1'

/**
 * Supported platform modes for tool visibility filtering.
 * TODO: Import from @amzn/jwo-store-management-platform-registration once published with SupportedPlatform export.
 */
type SupportedPlatform = 'meridian' | 'cloudscape'

export interface IZoneInfo {
    zone: string
    subzone: string
}

type IToolMenuConfig = IZone[]

let toolMenuConfig: IToolMenuConfig
let toolNameToDetailMap: Map<string, ITool> = new Map()
let zoneNameToDetailMap: Map<string, IZone> = new Map()
let subzoneNameToDetailMap: Map<string, ISubzone> = new Map()
let zoneToSubzoneMap: Map<string, string[]> = new Map()
let subzoneToToolMap: Map<string, string[]> = new Map()
const toolNameToZoneInfoMap: Map<string, IZoneInfo[]> = new Map()

const SEAWARD_TOOL_NAME = 'Welcome'
const SEAWARD_ATUI_PATH = `/remote/ihm-seaward/index.html?headless#/menu-v3`
const TASKS_HOME_PAGE_DISPLAY_NAME = 'Tasks'
const TASKS_HOME_PAGE_TOOL_NAME = 'space_dock'
const TASKS_HOME_PAGE_PATH = '/remote/space_dock/space_dock/index.html'

// https://tiny.amazon.com/2co6itn1/codeamazpackIhmAblobHEADsrc
export const HIDDEN_TOOLS = [
    'status_page_v2',
    'zone_tasks_tool',
    'high_priority_replen_list_tool',
    'handoff_pick_to_stage',
    'expiry_awareness_wfm',
    'ihm_wall_to_wall_tool',
    'tmoe_tasks_tool_redirect',
    'grocery_site_assistant_tool',
    // 'batch_tool_redirect'
]

// Overrides the position of the tool in the subzone, which is normally alphabetical order
// Order this from least-to-most important, as updates are in-place
export const POSITION_OVERRIDES: Array<[string, number]> = [
    ['ihm_virtual_store_tool_react', 0], // https://issues.amazon.com/issues/D299572505
    ['jwo-store-health-app', 5], // Move Store Health above Gate Assist in hamburger menu
]

// tslint:disable: max-line-length
// Uncomment below for local testing
// const SEAWARD_LOCAL_ENDPOINT = `https://localhost.aka.corp.amazon.com:4567/?domain=${getDomain()}&storeId=${getStoreId()}&region=${getRegion()}&auth=${_getParameterByName('auth')}&headless`
// const TASK_HOME_PAGE_LOCAL_ENDPOINT = `https://localhost.aka.corp.amazon.com:4569/index.html?domain=${getDomain()}&storeId=${getStoreId()}&region=${getRegion()}&auth=${_getParameterByName('auth')}`

export const convertObjToParams = (paramObj: any): string => {
    let paramCounter = 0
    let queryString = ''
    for (const [key, value] of Object.entries(paramObj)) {
        if (paramCounter !== 0 && value) {
            queryString += '&'
        }

        if (value && typeof value === 'string') {
            // Need to encodeURIComponent so that URI params do not get lost to tool iframe
            queryString += `${key}=${encodeURIComponent(value)}`
        }

        paramCounter++
    }

    return queryString
}

export const getToolDetailsById = (toolId: string) => {
    const tool = getToolById(toolId)
    if (!tool || !tool?.displayName) {
        return null
    }
    return toolNameToDetailMap.get(tool.displayName)
}

export const getToolUrl = (toolName: string, toolId?: string): string => {
    const entries = new URLSearchParams(location.search)
    // `region` is destructured out and deliberately discarded — it names the cell SMP itself was
    // loaded from, not the cell owning the selected store, which is frequently a different one.
    // See "Region and Cell Routing" in AGENTS.md.
    const { domain, region: _landingRegion, storeId, ...customParams } = Object.fromEntries(entries as any)

    // Use Redux storeId if available (including NO_CURRENT_STORE)
    const reduxStoreId = store?.getState?.()?.store ? getStoreIdFromRedux(store.getState()) : null
    const effectiveStoreId = reduxStoreId || NO_CURRENT_STORE

    // On initial load, redux store may not exist yet, this is fine, so we add check to avoid NPE
    const currentTool: any = store?.getState?.()?.tool ? getSelectedTool(store.getState()) : {}
    const paramsForURI = { ...customParams, ...(currentTool?.toolParameters || {}) }
    const hashPath = currentTool?.hashPath

    const baseUrl = getToolUrlForDomain(toolName, toolId)
    const params = buildParams(toolName, domain, getRegion(), effectiveStoreId, paramsForURI)
    const url = buildUrl(baseUrl, params, hashPath)

    return url
}

/**
 * Checks local dev config for a tool URL override.
 * Configured in src/config/local.dev.config.ts (gitignored).
 */
const getLocalOverrideUrl = (toolName: string, toolId?: string): string | null => {
    const { toolOverrides } = _getLocalDevConfig()
    if (!toolOverrides) return null
    const toolDetails = toolNameToDetailMap.get(toolName)
    const candidates = [toolId, toolDetails?.displayNameStringId, toolName].filter(Boolean)
    for (const key of candidates) {
        if (toolOverrides[key!]) return toolOverrides[key!]
    }
    return null
}

export const getToolUrlForDomain = (toolName: string, toolId?: string) => {
    const localOverride = getLocalOverrideUrl(toolName, toolId)
    if (localOverride) {
        return localOverride
    }

    let url = ''
    if (toolName === SEAWARD_TOOL_NAME) {
        url = SEAWARD_ATUI_PATH
        // Uncomment below for local testing
        // return SEAWARD_LOCAL_ENDPOINT

        // Force iframe loading status to SUCCESS since we're not using Seaward
        import('@atredux/store').then(({ store: reduxStore }) => {
            import('@atredux/slices/iframeSlice').then(({ setIsSeawardFrameLoading, seawardIFrameLoadingStatus }) => {
                reduxStore.dispatch(setIsSeawardFrameLoading(seawardIFrameLoadingStatus.SUCCESS))
            })
        })

    } else if (toolName === TASKS_HOME_PAGE_DISPLAY_NAME) {
        url = TASKS_HOME_PAGE_PATH
        // Uncomment below for local testing
        // return TASK_HOME_PAGE_LOCAL_ENDPOINT

    } else {
        if (toolId && HIDDEN_TOOLS.includes(toolId)) {
            const tool = getToolById(toolId)
            url = tool?.migratedPath || tool?.path
        } else {
            const toolDetails = toolNameToDetailMap.get(toolName)
            url = toolDetails?.migratedPath || toolDetails?.path
        }
    }

    if (typeof url !== 'string') {
        return null
    }

    return url
}

export const buildParams = (toolName: string, domain?: string, region?: string, storeId?: string, customParams?: any) => {
    const params = JSON.parse(JSON.stringify(customParams || {})) || {}
    params.domain = domain
    params.region = region || EnvironmentDetail.getRegion()

    // Associate Entry uses selectedStoreId (falling back to currentStoreId).
    // View-only tools also prefer selectedStoreId, so picking an unscanned store opens it
    // read-only (see getLaunchStoreId). All other tools use currentStoreId first — the store
    // scan required screen handles blocking tools when currentStoreId is NO_CURRENT_STORE.
    const state = store?.getState?.()
    const selectedStoreId = state ? getSelectedStoreId(state) : undefined
    const currentStoreId = state ? getStoreIdFromRedux(state) : undefined

    // Filter NO_CURRENT_STORE from URL param fallback — tools receiving this literal
    // string will attempt API calls that fail auth (P433688697).
    const validFallbackStoreId = storeId && storeId !== NO_CURRENT_STORE ? storeId : undefined

    const viewOnlyTool = isViewOnlySupportedTool(toolNameToDetailMap.get(toolName) || { displayName: toolName })

    if (toolName === 'Associate Entry') {
        const validCurrentStoreId = currentStoreId && currentStoreId !== NO_CURRENT_STORE
            ? currentStoreId
            : undefined
        const effectiveStoreId = selectedStoreId || validCurrentStoreId || validFallbackStoreId
        params.storeId = effectiveStoreId
        console.log('SMP: Associate Entry - using effectiveStoreId:', effectiveStoreId,
            '(selectedStoreId:', selectedStoreId, ', validCurrentStoreId:', validCurrentStoreId, ')')
        params.hasCurrentStore = !!currentStoreId && currentStoreId !== NO_CURRENT_STORE && effectiveStoreId !== currentStoreId
    } else {
        const validCurrentStoreId = currentStoreId && currentStoreId !== NO_CURRENT_STORE
            ? currentStoreId
            : undefined
        // View-only tools skip the storeId-argument fallback so the URL uses exactly the
        // getLaunchStoreId rule the view-only selectors use; other tools keep mainline behavior.
        // Same rule, not the same timing: the selectors are Redux-subscribed, but this URL is
        // only rebuilt when IFrameController re-renders (tool switch or flag load), not on a
        // scan-status change. That is safe in practice, because the scan-in happens in Associate
        // Entry and switching back rebuilds the URL, and a stale URL only stays read-only.
        params.storeId = getLaunchStoreId(
            viewOnlyTool,
            validCurrentStoreId,
            selectedStoreId,
        ) || (viewOnlyTool ? undefined : validFallbackStoreId)
    }

    // The region must name the cell of the store id this URL carries, which is not always the
    // session's cell — the two storeId precedences above can resolve to stores in different cells
    // (AGENTS.md, "Region and Cell Routing"). Only a recorded cell overrides; for a store we have
    // not resolved yet there is nothing better than the session's own.
    const storeRegion = getRegionForStore(params.storeId)
    if (storeRegion) {
        params.region = storeRegion
    }

    params.locale = store?.getState?.()?.locale ? getSelectedLocale(store.getState()) : 'en-US'

    // StoreFamily is also required for 3P flow
    if (EnvironmentDetail.isIhmIdentityBrokerAuthN()) {
        params.storeFamily = EnvironmentDetail.getStoreFamily()
    }

    if (toolName === SEAWARD_TOOL_NAME && params.initialTool) {
        delete params.initialTool

        if (params.initialZoneName) {
            delete params.initialZoneName
        }

        if (params.initialSubzoneName) {
            delete params.initialSubzoneName
        }
    }

    if (_getParameterByName('auth')) {
        params.auth = _getParameterByName('auth')
    }

    // Signal to tools that they're hosted by SMP (enables PlatformClient transport)
    params.container = 'smp'

    // View-only: a tool registered viewOnlySupported, launched against a store the user has
    // not scanned into, opens read-only instead of behind the store-scan modal. One-way:
    // absence means editable, so the tool drops back to edit mode on the next launch after
    // scan-in (IFrameController rebuilds this URL on every render).
    if (state && viewOnlyTool
        && !(params.storeId && getStoreScanStatus(state, params.storeId))) {
        params.viewOnly = 'true'
    }

    return params
}

const buildUrl = (url: string, params?: any, hashPath?: string) => {
    if (!url) {
        return null
    }

    console.log('SMP: buildUrl - input url:', url, 'params:', params)

    if (params) {
        const indexOfHashChar = url.indexOf('#')
        const indexOfQueryChar = url.indexOf('?')

        // Stores the url before the hash '#' symbol
        let urlBeforeHashSymbol = (indexOfHashChar >= 0) ? url.substring(0, indexOfHashChar) : url
        // Stores the substring from the '#' symbol
        const hashedUrlSegment = (indexOfHashChar >= 0) ? url.substring(indexOfHashChar) : ''

        // Parse existing query parameters if they exist
        let existingParams = new URLSearchParams()
        if (indexOfQueryChar >= 0) {
            const existingQueryString = urlBeforeHashSymbol.substring(indexOfQueryChar + 1)
            existingParams = new URLSearchParams(existingQueryString)
            urlBeforeHashSymbol = urlBeforeHashSymbol.substring(0, indexOfQueryChar)
        }

        // Merge params, with new params overriding existing ones
        Object.keys(params).forEach((key) => {
            if (params[key] !== undefined && params[key] !== null) {
                existingParams.set(key, params[key])
            }
        })

        // Build final URL
        const queryString = existingParams.toString()
        url = queryString ? `${urlBeforeHashSymbol}?${queryString}${hashedUrlSegment}` : `${urlBeforeHashSymbol}${hashedUrlSegment}`
    }

    if (hashPath && _.isString(hashPath) && hashPath.length) {
        // Append hashPath as a URL fragment. A '#' separator is REQUIRED: without it
        // the hashPath is concatenated directly onto the query string, corrupting the
        // last query param (e.g. '...&region=pdx' + '/sales' => 'region=pdx/sales').
        // That broke EnvironmentDetail.getRegion() inside the iframe, which in turn
        // made RUM cell lookup (`${domain}-${region}`) miss and silently drop metrics.
        // If the url already has a fragment, append directly so we don't emit a second '#'.
        const urlAlreadyHasFragment = url.indexOf('#') >= 0
        const hashStartsWithHash = hashPath.startsWith('#')
        if (urlAlreadyHasFragment || hashStartsWithHash) {
            url = url + (hashStartsWithHash && urlAlreadyHasFragment ? hashPath.substring(1) : hashPath)
        } else {
            url = url + '#' + hashPath
        }
    }

    console.log('SMP: buildUrl - final url:', url)
    return url
}

// tslint:disable: max-line-length
// Uncomment below for local testing
// export const testTool: ITool = {
//     displayName: 'Migrated Tool',
//     displayNameStringId: '',
//     metadata: { cti: '', package: '' },
//     path: `https://localhost.aka.corp.amazon.com:8080`,
//     migratedPath: `https://localhost.aka.corp.amazon.com:8080`,
//     isMigrated: true,
//     showMigrated: true,
// }

// --- Centralized asset registry ---
const assetsProvider = new RegisteredAssetsProvider()

/**
 * Builds the full URL for an asset from the centralized registry.
 * On a regional app host: {current origin}/assets/{assetId}/index.html (same distribution as the shell).
 * Otherwise: https://{awsRegion}.{domain}.storemanagement.jihm.amazon.dev/assets/{assetId}/index.html
 */
const getAssetUrl = (asset: AssetRegistration): string =>
    `${getAssetBaseUrl(asset.assetId)}/index.html`

/**
 * Store-scan tiers. Every tool falls into exactly one (see getStoreScanPolicy):
 * - 'exempt'   — usable without a scan (STORE_SCAN_EXEMPT_IDS below).
 * - 'viewOnly' — viewable without a scan, edits need one. Opted in through the asset
 *                registration (AssetBuilder.withViewOnlySupported); Planogram is the first.
 * - 'required' — blocked by StoreScanRequiredModal until the user scans in. The default.
 */
export type StoreScanPolicy = 'exempt' | 'viewOnly' | 'required'

/** Tools usable without a store scan. View-only tools opt in via their registration; exempt wins. */
const STORE_SCAN_EXEMPT_IDS = new Set([
    'ihm-associate-entry-react-website',
    'jwo-user-management-tool',
    'ihm-jwo-training-academy-tools-website',
])

/**
 * Converts an AssetRegistration from the centralized registry into an ITool.
 */
const assetToTool = (asset: AssetRegistration): ITool => {
    const url = getAssetUrl(asset)
    return {
        displayName: asset.displayName,
        displayNameStringId: asset.assetId,
        metadata: { cti: asset.metadata.cti, package: asset.metadata.packageName },
        path: url,
        migratedPath: url,
        isMigrated: true,
        showMigrated: true,
        skipStoreScan: STORE_SCAN_EXEMPT_IDS.has(asset.assetId),
        viewOnlySupported: asset.viewOnlySupported === true,
        icon: getAssetIconUrl(asset),
    }
}

/**
 * Whether a tool opted into view-only through its asset registration.
 *
 * The flag on the tool object is checked first. A tool that also exists in the legacy
 * menu config lands in toolNameToDetailMap as that icon- and flag-less entry, so the
 * registry is the fallback, matched by display name (the key the rest of the shell uses).
 *
 * The whole tier is gated on VIEW_ONLY_TOOLS_FLAG; with it off, an opted-in tool is 'required'.
 * The flag lives in the feature-flag cache, which React and Redux can't see, so callers that
 * render this decision must subscribe to it: selectors via useViewOnlySelector, other
 * components via useFeatureFlag(VIEW_ONLY_TOOLS_FLAG). A plain useSelector won't update.
 */
export const isViewOnlySupportedTool = (
    tool?: Pick<ITool, 'displayName' | 'viewOnlySupported' | 'skipStoreScan'> | null,
): boolean => {
    // A store-scan-exempt tool is never gated on scan status, so it is never view-only.
    if (!tool || tool.skipStoreScan === true) {
        return false
    }
    const optedIn = tool.viewOnlySupported === true || getViewOnlyToolNames().has(tool.displayName)
    // Read last, so only opted-in tools touch the flag (never the module-init buildParams call,
    // whose empty-cache read would start a Gateway fetch before login).
    return optedIn && featureFlagService.get(VIEW_ONLY_TOOLS_FLAG).enabled
}

/**
 * Which store-scan tier a tool is in (see StoreScanPolicy). Reads VIEW_ONLY_TOOLS_FLAG through
 * isViewOnlySupportedTool, so the same subscription rule applies to callers that render it.
 */
export const getStoreScanPolicy = (
    tool?: Pick<ITool, 'displayName' | 'viewOnlySupported' | 'skipStoreScan'> | null,
): StoreScanPolicy => {
    if (tool?.skipStoreScan === true) {
        return 'exempt'
    }
    return isViewOnlySupportedTool(tool) ? 'viewOnly' : 'required'
}

/**
 * Returns all registered assets as ITool objects, filtered by the current platform mode.
 *
 * Platform support is resolved per-domain via `resolveSupportedPlatforms`: an
 * asset is included when its platforms for the current asset domain contain the
 * current mode. Assets without a per-domain map default to both modes
 * (backward compatible).
 *
 * Requirements: 5.2–5.4
 */
const getRegisteredTools = (): ITool[] => {
    try {
        const currentPlatform = getCurrentPlatformMode()
        const platformDomain = getDomainFromHostname() as PlatformDomain
        const assetDomain = getAssetDomain(platformDomain) || platformDomain
        return assetsProvider.getAssetsForDomain(assetDomain)
            .filter((asset) =>
                resolveSupportedPlatforms(asset, assetDomain as Domain).includes(currentPlatform),
            )
            .map(assetToTool)
    } catch (error) {
        console.error('SMP: Failed to load tools from centralized registry:', error)
        return []
    }
}

/**
 * Display names of registered view-only tools. isViewOnlySupportedTool runs inside Redux
 * selectors on every dispatch, so the set is built once per platform mode and asset domain
 * (the only inputs to getRegisteredTools that can change) instead of on every check.
 */
let viewOnlyToolNamesCache: { key: string, names: Set<string> } | undefined
const getViewOnlyToolNames = (): Set<string> => {
    let key: string
    try {
        const platformDomain = getDomainFromHostname() as PlatformDomain
        key = `${getCurrentPlatformMode()}|${getAssetDomain(platformDomain) || platformDomain}`
    } catch {
        // Reached during module init (a URL constant below calls buildParams before the
        // registry helpers are defined). Answer "none" without caching, as getRegisteredTools does.
        return new Set()
    }
    if (viewOnlyToolNamesCache?.key !== key) {
        viewOnlyToolNamesCache = {
            key,
            names: new Set(getRegisteredTools()
                .filter((t) => t.viewOnlySupported === true && t.skipStoreScan !== true)
                .map((t) => t.displayName)),
        }
    }
    return viewOnlyToolNamesCache.names
}

/** Test-only: the registry is static in the bundle, but tests swap it per case. */
export const resetViewOnlyToolNamesCache = (): void => {
    viewOnlyToolNamesCache = undefined
}

/**
 * Returns the Associate Entry tool from the centralized registry.
 * Used by StoreSelectionPage and StoreSwitcherHeader to navigate after store selection.
 */
export const getAssociateEntryTool = (): ITool | undefined => {
    const tools = getRegisteredTools()
    return tools.find((t) => t.displayName === 'Associate Entry')
}

// Backward-compatible export — use getAssociateEntryTool() for new code.
// Note: skipStoreScan is already set via assetToTool() → STORE_SCAN_EXEMPT_IDS
// because this Proxy delegates all property access to the registered tool.
// tslint:disable-next-line: no-any
export const ihmAssociateEntryTool: ITool = new Proxy({} as ITool, {
    get (_target, prop) {
        const tool = getAssociateEntryTool()
        return tool ? tool[prop] : undefined
    },
})

export const tasksTool: ITool = {
    displayName: TASKS_HOME_PAGE_DISPLAY_NAME,
    displayNameStringId: '', // migrated-ihm-associate-tool-tasks-display-name
    metadata: { cti: '', package: '' },
    path: getToolUrl(TASKS_HOME_PAGE_DISPLAY_NAME),
    migratedPath: getToolUrl(TASKS_HOME_PAGE_DISPLAY_NAME),
    isMigrated: true,
    showMigrated: true,
    toolName: TASKS_HOME_PAGE_TOOL_NAME,
}

export const seawardTool: ITool = {
    displayName: SEAWARD_TOOL_NAME,
    displayNameStringId: '',
    metadata: { cti: '', package: '' },
    path: getToolUrl(SEAWARD_TOOL_NAME),
    isMigrated: false,
    showMigrated: false,
    skipStoreScan: true,
}

export const seawardReactTool: ITool = {
    displayName: SEAWARD_TOOL_NAME,
    displayNameStringId: '',
    metadata: { cti: '', package: '' },
    path: getToolUrl(SEAWARD_TOOL_NAME),
    isMigrated: false,
    showMigrated: true,
    skipStoreScan: true,
}

// Operations is an in-portal Cloudscape tabs page (OperationsPage), not a tool iframe.
// Selecting it in the sidebar renders OperationsPage inside ToolLayout. Modeled after seawardTool
// so _navigateToTool hides the tool iframe and the portal page renders in its place. It has no
// toolName so ToolLayout's cloudscape collapse guard keeps the shell visible.
export const OPERATIONS_TOOL_NAME = 'Operations'

export const operationsTool: ITool = {
    displayName: OPERATIONS_TOOL_NAME,
    displayNameStringId: '',
    metadata: { cti: '', package: '' },
    path: '/operations',
    isMigrated: false,
    showMigrated: false,
    skipStoreScan: true,
}

export const isNewHomepageTool = (tool: ITool) => {
    return tool?.toolName === TASKS_HOME_PAGE_TOOL_NAME
}

/**
 * Builds a zone containing all tools from the centralized registry
 * and injects it into the menu config. This makes the registry the
 * source of truth for tool registration — no backend dependency needed.
 */
// Registered tools that should remain reachable via cross-frame setCurrentTool
// (so deep links from other tools resolve through getToolDetailsById and end
// up using the migrated iframe with a full asset URL) but stay hidden from
// the associate-facing sidebar menu. Matched against displayNameStringId
// (which is set to asset.assetId in assetToTool).
//
// Tools listed here are still injected into the menu config and populate
// toolNameToDetailMap; only the sidebar-facing maps (subzoneToToolMap and
// toolNameToZoneInfoMap) skip them so they never render as menu options.
//
// To reenable in the sidebar, remove the ID from SIDEBAR_HIDDEN_TOOLS.
export const SIDEBAR_HIDDEN_TOOLS: string[] = []

const isDebugMode = (): boolean =>
    new URLSearchParams(window.location.search).get('debugMode') === 'true'

// Tools hidden on React Native (BYOD) devices -- not in scope for current milestone
// https://t.corp.amazon.com/P436797294
// TODO: Move platform support to tool registration:
// https://app.asana.com/1/8442528107068/project/1214725336709632/task/1215166711201381?focus=true
const REACT_NATIVE_HIDDEN_TOOLS: string[] = [
    'jwo-store-management-portal',
]

/**
 * Returns the current platform mode from the URL query parameter.
 * Only exact case-sensitive 'cloudscape' returns 'cloudscape'; all other values
 * (including absent, 'meridian', or any other value) return 'meridian'.
 *
 * Requirements: 1.1–1.5, 5.3–5.4
 */
export const getCurrentPlatformMode = (): SupportedPlatform => {
    const platform = new URLSearchParams(window.location.search).get('platform')
    return platform === 'meridian' ? 'meridian' : 'cloudscape'
}

/**
 * Returns true when the tool should be excluded from the sidebar UI but kept
 * available for cross-frame deep-link lookups. Debug mode bypasses this so
 * developers can verify the tool end-to-end from the menu.
 */
export const isSidebarHiddenTool = (tool: ITool | undefined): boolean => {
    if (!tool) {
        return false
    }
    if (isDebugMode()) {
        return false
    }
    if (SIDEBAR_HIDDEN_TOOLS.includes(tool.displayNameStringId)) {
        return true
    }
    if (isReactNativeApp() && REACT_NATIVE_HIDDEN_TOOLS.includes(tool.displayNameStringId)) {
        return true
    }
    return false
}

const injectRegisteredToolsZone = (menuConfig: IToolMenuConfig): void => {
    // All registered tools are added to the menu config so their entries land
    // in toolNameToDetailMap and getToolDetailsById can resolve them. Tools
    // listed in SIDEBAR_HIDDEN_TOOLS are filtered out later, at sidebar map
    // generation time, so they remain invisible to associates browsing the
    // menu while still being reachable via cross-frame setCurrentTool.
    const registeredTools = getRegisteredTools()
    if (registeredTools.length === 0) {
        return
    }

    const toolsZone: IZone = {
        zoneName: 'Tools',
        name: 'Tools',
        displayName: 'Tools',
        zoneDisplayName: 'Tools',
        displayNameStringId: 'menu-zone-tools',
        subzones: [
            {
                subzoneName: 'All Tools',
                name: 'All Tools',
                displayName: 'All Tools',
                subzoneDisplayName: 'All Tools',
                displayNameStringId: 'menu-subzone-all-tools',
                tools: registeredTools.map((tool) => ({
                    ...tool,
                    translateID: tool.displayNameStringId,
                    showMigrated: true,
                })),
            } as ISubzone,
        ],
    } as IZone

    menuConfig.push(toolsZone)
    console.log(`SMP: Injected ${registeredTools.length} tools from centralized registry into menu config`)
}

export const getMenuConfig = (fetchedToolMenuConfig: IToolMenuConfig): Promise<IToolMenuConfig> => {
    const deferred = generateDeferredPromise<IToolMenuConfig>()
    populateToolsForZoneConfig(fetchedToolMenuConfig)

    // Inject registered tools as a proper zone in the menu config
    injectRegisteredToolsZone(fetchedToolMenuConfig)

    toolMenuConfig = fetchedToolMenuConfig
    console.info('SMP: Updated tool menu config : ' + JSON.stringify(toolMenuConfig))

    filterMenuConfig()
    generateToolNameToDetailMap()
    generateZoneToSubzoneMap()
    generateSubzoneToToolMap()
    generateToolToZoneInfoMap()

    // Remove "Migrated Tool" to not show on the Menu.
    if (toolMenuConfig) {
        toolMenuConfig.forEach((zone) => {
            zone.subzones.forEach((subzone) => {
                subzone.tools = subzone.tools.filter((tool) => tool.displayName !== 'Migrated Tool')
            })
        })

        if (toolNameToDetailMap.has('Migrated Tool')) {
            toolNameToDetailMap.delete('Migrated Tool')
            console.log('SMP: Removed Migrated Tool from toolNameToDetailMap')
        }
    }

    deferred.resolve(toolMenuConfig)

    return deferred.promise
}

const getAllTools = (): any[] => {
    if (!toolMenuConfig) return []

    const allTools: any[] = []
    toolMenuConfig.forEach((zone) => {
        zone.subzones.forEach((subzone) => {
            allTools.push(...subzone.tools)
        })
    })

    return allTools
}

const filterMenuConfig = (): void => {
    if (!toolMenuConfig) {
        console.error('SMP: No tool menu config detected.')
        return
    }

    toolMenuConfig.forEach((zone) => {
        zone.subzones.forEach((subzone) => {
            // Remove tools that don't have a valid path
            subzone.tools = subzone.tools.filter((tool) => !!tool?.path || !!tool?.migratedPath)
        })

        // Remove empty subzones
        zone.subzones = zone.subzones.filter((subzone) => subzone?.tools?.length > 0)
    })
}

const generateToolNameToDetailMap = (): void => {
    toolNameToDetailMap = new Map()

    if (!toolMenuConfig) {
        console.error('SMP: No tool menu config detected.')
        return
    }

    toolNameToDetailMap.set(seawardTool.displayName, seawardTool)
    toolNameToDetailMap.set(tasksTool.displayName, tasksTool)
    toolMenuConfig.forEach((zone) => {
        zone.subzones.forEach((subzone) => {
            subzone.tools.forEach((tool) => {
                const existing = toolNameToDetailMap.get(tool.displayName)
                if (!existing) {
                    toolNameToDetailMap.set(tool.displayName, tool)
                } else if (!existing.icon && tool.icon) {
                    // The legacy zone lands first, so a tool in both kept the icon-less entry.
                    // Replace rather than mutate: the map holds exported singletons.
                    toolNameToDetailMap.set(tool.displayName, { ...existing, icon: tool.icon })
                }
            })
        })
    })

    // Uncomment for testing migrated tool
    // toolNameToDetailMap.set(testTool.displayName, testTool)

    console.log('SMP: Tool name to detail map generated: ' + JSON.stringify(Object.fromEntries(toolNameToDetailMap)))
}

const generateZoneToSubzoneMap = (): void => {
    zoneToSubzoneMap = new Map()
    zoneNameToDetailMap = new Map()

    if (!toolMenuConfig) {
        console.error('SMP: No tool menu config detected.')
        return
    }

    toolMenuConfig.forEach((zone) => {
        zone.translateID = zone.displayNameStringId ? zone.displayNameStringId : `menu-zone-${convertToDashed(zone.zoneName)}`
        zone.subzones.forEach((subzone) => {
            if (!zoneToSubzoneMap.get(zone.zoneName)) {
                zoneToSubzoneMap.set(zone.zoneName, [])
            }

            const zoneArray = Array.from(zoneToSubzoneMap.get(zone.zoneName))
            if (!zoneArray.includes(subzone.subzoneName)) {
                zoneArray.push(subzone.subzoneName)
            }

            zoneToSubzoneMap.set(zone.zoneName, zoneArray.slice(0))
        })

        zoneNameToDetailMap.set(zone.zoneName, zone)
    })

    console.log('SMP: Zone to subzone map generated: ' + JSON.stringify(Object.fromEntries(zoneToSubzoneMap)))
}

const overrideToolPosition = (toolId: string, targetPosition: number) => {
    const tool = getToolDetailsById(toolId)
    if (!tool) {
        return
    }

    const displayName = tool.displayName
    for (const [subzone, tools] of subzoneToToolMap) {
        const currentIndex = tools.indexOf(displayName)
        if (currentIndex !== -1) {
            const safeIndex = Math.min(Math.max(0, targetPosition), tools.length - 1)
            tools.splice(currentIndex, 1)
            tools.splice(safeIndex, 0, displayName)
        }
    }
}

const generateSubzoneToToolMap = (): void => {
    subzoneToToolMap = new Map()
    subzoneNameToDetailMap = new Map()

    if (!toolMenuConfig) {
        console.error('SMP: No tool menu config detected.')
        return
    }

    toolMenuConfig.forEach((zone) => {
        zone.subzones.forEach((subzone) => {
            subzone.translateID = subzone.displayNameStringId ? subzone.displayNameStringId : `menu-subzone-${convertToDashed(subzone.subzoneName)}`
            subzone.tools.forEach((tool) => {
                // Skip tools that should not appear in the sidebar UI (FMT/LCV
                // are still in toolNameToDetailMap so cross-frame deep links
                // resolve, but the menu doesn't surface them to associates).
                if (isSidebarHiddenTool(tool)) {
                    return
                }
                if (!subzoneToToolMap.get(`${zone.zoneName}.${subzone.subzoneName}`)) {
                    subzoneToToolMap.set(`${zone.zoneName}.${subzone.subzoneName}`, [])
                }

                const subzoneArray = Array.from(subzoneToToolMap.get(`${zone.zoneName}.${subzone.subzoneName}`))
                if (!subzoneArray.includes(tool.displayName)) {
                    subzoneArray.push(tool.displayName)
                    devLog('Tool VISIBLE:', tool.displayName, 'zone:', zone.zoneName)

                }

                subzoneToToolMap.set(`${zone.zoneName}.${subzone.subzoneName}`, subzoneArray.slice(0))
            })

            subzoneNameToDetailMap.set(subzone.subzoneName, subzone)
        })
    })

    for (const [overrideTool, overridePosition] of POSITION_OVERRIDES) {
        overrideToolPosition(overrideTool, overridePosition)
    }

    // Uncomment for testing migrated tool
    // const firstEntry = subzoneToToolMap.keys().next().value
    // const migratedArr = Array.from(subzoneToToolMap.get(firstEntry))
    // migratedArr.push(testTool.displayName)
    // subzoneToToolMap.set(firstEntry, migratedArr)

    console.log('SMP: Subzone to tool map generated: ' + JSON.stringify(Object.fromEntries(subzoneToToolMap)))
}

const generateToolToZoneInfoMap = (): void => {
    if (!toolMenuConfig) {
        console.error('SMP: No tool menu config detected.')
        return
    }

    toolMenuConfig.forEach((zone) => {
        zone.subzones.forEach((subzone) => {
            subzone.tools.forEach((tool) => {
                // Skip tools that should not appear in the sidebar UI; the
                // sidebar's frequently-used flow uses this map to look up
                // zone info per tool, so excluding them here keeps the
                // sidebar consistent with subzoneToToolMap.
                if (isSidebarHiddenTool(tool)) {
                    return
                }
                if (!toolNameToZoneInfoMap.get(tool.displayName)) {
                    toolNameToZoneInfoMap.set(tool.displayName, [])
                }

                const zoneInfoArray = Array.from(toolNameToZoneInfoMap.get(tool.displayName))
                if (!zoneInfoArray.some((zoneInfo) => zoneInfo.zone === zone.zoneName && zoneInfo.subzone === subzone.subzoneName)) {
                    zoneInfoArray.push({
                        zone: zone.zoneName,
                        subzone: subzone.subzoneName,
                    })
                }

                toolNameToZoneInfoMap.set(tool.displayName, zoneInfoArray)
            })
        })
    })

    // Uncomment for testing migrated tool
    // const firstEntry = subzoneToToolMap.keys().next().value
    // const testToolZoneInfoArray = []
    // testToolZoneInfoArray.push({
    //     zone: firstEntry.split('.')[0],
    //     subzone: firstEntry.split('.')[1],
    // })
    // toolNameToZoneInfoMap.set(testTool.displayName, testToolZoneInfoArray)

    console.log('SMP: Tool to zone info map generated: ' + JSON.stringify(Object.fromEntries(toolNameToZoneInfoMap)))
}

const populateToolsForZoneConfig = (zoneConfig: IToolMenuConfig): void => {
    zoneConfig.forEach((zone, index) => {
        if (zone.name && !zone.zoneName) {
            zone.zoneName = zone.name
            zoneConfig[index].zoneName = zone.name
        }

        if (zone.displayName && !zone.zoneDisplayName) {
            zone.zoneDisplayName = zone.displayName
            zoneConfig[index].zoneDisplayName = zone.displayName
        }
        zone.subzones.forEach((subzone, subzoneIndex) => {
            if (subzone.name && !subzone.subzoneName) {
                subzone.subzoneName = subzone.name
                zoneConfig[index].subzones[subzoneIndex].subzoneName = subzone.name
            }

            if (subzone.displayName && !subzone.subzoneDisplayName) {
                subzone.subzoneDisplayName = subzone.displayName
                zoneConfig[index].subzones[subzoneIndex].subzoneDisplayName = subzone.displayName
            }
            subzone.tools = subzone.tools.reduce((tools, toolId) => {
                // For direct ATMenu/ATRegistrations access (no ATCore), toolId is the ITool object
                if (typeof toolId !== 'string') {
                    toolId.translateID = toolId.displayNameStringId || toolId.translateID
                    toolId.showMigrated = true
                    tools.push(toolId)
                } else {
                    // ATCore GetMenu flow needs to look up the tool ID to get the ITool
                    const tool = getToolById(toolId)
                    if (tool) {
                        tool.translateID = tool.displayNameStringId || tool.translateID
                        tool.showMigrated = true
                        tools.push(tool)
                    } else {
                        console.error('SMP: Tool defined in menu missing from registry.', toolId)
                    }
                }
                return tools
            }, [])
        })
    })
}

export const getToolNameToDetailMap = (): typeof toolNameToDetailMap => {
    return toolNameToDetailMap
}

export const getZoneNameToDetailMap = (): typeof zoneNameToDetailMap => {
    return zoneNameToDetailMap
}

export const getSubzoneNameToDetailMap = (): typeof subzoneNameToDetailMap => {
    return subzoneNameToDetailMap
}

export const getZoneToSubzoneMap = (): typeof zoneToSubzoneMap => {
    return zoneToSubzoneMap
}

export const getSubzoneToToolMap = (): typeof subzoneToToolMap => {
    return subzoneToToolMap
}

export const getToolNameToZoneInfoMap = (): typeof toolNameToZoneInfoMap => {
    return toolNameToZoneInfoMap
}

export const getToolMenuConfig = (): typeof toolMenuConfig => {
    return toolMenuConfig
}

export const convertToDashed = (displayName: string) => {
    return displayName.toLowerCase().split(' ').join('-')
}

/**
 * Checks if a tool should be visible for the current store using the
 * centralized registration package's StoreAvailability data.
 *
 * Resolution order:
 * 1. If storeInfo not yet loaded → shown (re-evaluated after store selection)
 * 2. If the store is in the asset's denylistOverride → hidden
 * 3. If the store is in the asset's allowlistOverride → shown
 * 4. If storeFamilies set and store family not in list → hidden
 * 5. If storeTags set and not all present in store actual tags → hidden
 */
export const isAllowListedTool = (toolObject, _checkShowMigrated?: boolean) => {
    if (!toolObject) {
        return false
    }

    // Look up the asset in the centralized registry by display name
    let assets: AssetRegistration[]
    try {
        assets = assetsProvider.getAssetsForDomain(getAssetDomain(getDomainFromHostname() as PlatformDomain) || getDomainFromHostname())
    } catch (error) {
        console.error('SMP: Failed to load registry in isAllowListedTool:', error)
        return false
    }
    const asset = assets.find(
        (a) => a.displayName === toolObject.displayName,
    )

    // If the tool isn't in the registry, default to hidden
    if (!asset) {
        return false
    }

    const { storeFamilies, storeTags, allowlistOverride, denylistOverride } = asset.availability
    const storeId = getStoreId()
    const storeInfo = getStoreInfo(store.getState())

    // No availability constraints → show for all stores
    if ((!storeFamilies || storeFamilies.length === 0) && (!storeTags || storeTags.length === 0) && (!allowlistOverride || allowlistOverride.length === 0) && (!denylistOverride || denylistOverride.length === 0)) {
        return true
    }

    // Denylist takes highest precedence at the store level
    if (denylistOverride?.includes(storeId)) {
        return false
    }

    // Allowlist override grants access regardless of tags
    if (allowlistOverride?.includes(storeId)) {
        return true
    }

    // NEW: if storeFamilies is set, use new logic (family OR + tags AND)

    // If store info not yet loaded, show all tools (will re-evaluate after store selection)
    if (!storeInfo || !storeInfo.storeFamily) {
        console.info('[SMP] isAllowListedTool:', toolObject.displayName, '| storeInfo not loaded, showing by default')
        return true
    }

    devLog('isAllowListedTool:', toolObject.displayName, '| family:', storeInfo.storeFamily, '| required tags:', storeTags, '| store tags:', storeInfo?.tags?.length)
    if (storeFamilies && storeFamilies.length > 0) {
        // Family check (OR)
        if (!storeFamilies.includes(storeInfo?.storeFamily)) {
            return false
        }
        // Tags check: ALL storeTags must be in store actual tags (AND)
        if (storeTags && storeTags.length > 0) {
            const actualStoreTags: string[] = storeInfo.tags || []
            const allPresent = storeTags.every((tag) => actualStoreTags.includes(tag))
            if (!allPresent) {
                console.info('[SMP] Tool hidden (missing tags):', toolObject.displayName, '| requires:', storeTags)
                return false
            }
        }
        return true
    }

    return false
}
